import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  admin: 'bg-purple-100 text-purple-800',
  profesor: 'bg-blue-100 text-blue-800',
  estudiante: 'bg-green-100 text-green-800',
  pastor: 'bg-yellow-100 text-yellow-800',
  graduado: 'bg-pink-100 text-pink-800',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-UY', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

const daysLabel = (d) => (d === null || d === undefined) ? '—' : `${d} d`;

const daysColor = (d) => {
  if (d === null || d === undefined) return 'text-gray-400';
  if (d >= 90) return 'text-red-600 font-bold';
  if (d >= 60) return 'text-orange-600 font-semibold';
  if (d >= 30) return 'text-yellow-700';
  return 'text-green-700';
};

const ActividadUsuarios = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [rolFilter, setRolFilter] = useState('todos');
  const [sortBy, setSortBy] = useState('dias_desde_ultima_senal');
  const [sortDir, setSortDir] = useState('asc');
  const [showInactive, setShowInactive] = useState('todos'); // todos | activos | inactivos | nunca

  const cargar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/admin/usuarios/actividad`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setUsers(d.users || []);
    } catch (e) {
      toast.error('No pude cargar la actividad de usuarios');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const sincronizar = async () => {
    if (!window.confirm('Sincronizar última actividad usando el último login de cada usuario. Esto corrige avisos falsos. ¿Continuar?')) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/admin/usuarios/sync-actividad`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Error');
      toast.success(`Actualizados: ${d.updated} · Avisos limpiados: ${d.aviso_cleared}`);
      await cargar();
    } catch (e) {
      toast.error('Sincronización falló');
    } finally { setSyncing(false); }
  };

  const filtered = useMemo(() => {
    let list = [...users];
    if (rolFilter !== 'todos') list = list.filter(u => u.rol === rolFilter);
    if (showInactive === 'activos') list = list.filter(u => u.activo);
    else if (showInactive === 'inactivos') list = list.filter(u => !u.activo);
    else if (showInactive === 'nunca') list = list.filter(u => u.nunca_ingreso);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.nombre || '').toLowerCase().includes(q) ||
        (u.apellido || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [users, search, rolFilter, showInactive, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const arrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const stats = useMemo(() => ({
    total: users.length,
    nunca: users.filter(u => u.nunca_ingreso).length,
    inactivos: users.filter(u => !u.activo).length,
    en_riesgo: users.filter(u => (u.dias_desde_ultima_senal ?? 0) >= 60 && (u.dias_desde_ultima_senal ?? 0) < 90 && u.activo).length,
  }), [users]);

  return (
    <div className="p-4 sm:p-6 max-w-full" data-testid="actividad-usuarios-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          <i className="fas fa-user-clock text-purple-600 mr-2" />
          Actividad de Usuarios
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Último login y última señal (heartbeat) de cada cuenta. Usá esto para verificar antes de acciones automáticas.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500">Total</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500">Nunca ingresaron</div>
          <div className="text-2xl font-bold text-gray-500">{stats.nunca}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500">En riesgo (60-89d)</div>
          <div className="text-2xl font-bold text-orange-600">{stats.en_riesgo}</div>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500">Desactivados</div>
          <div className="text-2xl font-bold text-red-600">{stats.inactivos}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4 items-center bg-white p-3 rounded-lg shadow-sm border border-gray-100">
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm"
          data-testid="actividad-search"
        />
        <select value={rolFilter} onChange={(e) => setRolFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="actividad-rol-filter">
          <option value="todos">Todos los roles</option>
          <option value="admin">Admin</option>
          <option value="profesor">Profesor</option>
          <option value="estudiante">Estudiante</option>
          <option value="pastor">Pastor</option>
          <option value="graduado">Graduado</option>
        </select>
        <select value={showInactive} onChange={(e) => setShowInactive(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="actividad-estado-filter">
          <option value="todos">Todos</option>
          <option value="activos">Solo activos</option>
          <option value="inactivos">Solo inactivos</option>
          <option value="nunca">Nunca ingresaron</option>
        </select>
        <button
          onClick={cargar}
          disabled={loading}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm disabled:opacity-50"
          data-testid="actividad-reload-btn"
        >
          <i className="fas fa-sync-alt mr-1" /> Recargar
        </button>
        <button
          onClick={sincronizar}
          disabled={syncing}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm disabled:opacity-50"
          title="Copia ultima_conexion → ultima_actividad cuando el login es más reciente. Corrige avisos falsos."
          data-testid="actividad-sync-btn"
        >
          <i className="fas fa-wand-magic-sparkles mr-1" /> {syncing ? 'Sincronizando…' : 'Sincronizar actividad'}
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('email')}>Usuario{arrow('email')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('rol')}>Rol{arrow('rol')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('ultima_conexion')}>Último login{arrow('ultima_conexion')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dias_desde_login')}>Hace{arrow('dias_desde_login')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('ultima_actividad')}>Última señal{arrow('ultima_actividad')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dias_desde_actividad')}>Hace{arrow('dias_desde_actividad')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-400">Cargando…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-400">Sin resultados.</td></tr>
            )}
            {!loading && filtered.map((u) => (
              <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`actividad-row-${u.email}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {u.foto_perfil ? (
                      <img src={u.foto_perfil} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                        {(u.nombre || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{u.nombre} {u.apellido || ''}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[u.rol] || 'bg-gray-100 text-gray-700'}`}>{u.rol}</span>
                </td>
                <td className="px-3 py-2 text-gray-700">{fmtDate(u.ultima_conexion)}</td>
                <td className={`px-3 py-2 ${daysColor(u.dias_desde_login)}`}>{daysLabel(u.dias_desde_login)}</td>
                <td className="px-3 py-2 text-gray-700">{fmtDate(u.ultima_actividad)}</td>
                <td className={`px-3 py-2 ${daysColor(u.dias_desde_actividad)}`}>{daysLabel(u.dias_desde_actividad)}</td>
                <td className="px-3 py-2">
                  {u.nunca_ingreso ? (
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">Nunca ingresó</span>
                  ) : u.activo ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Activo</span>
                  ) : u.desactivado_automaticamente ? (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">Auto-desactivado</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">Desactivado</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        <b>Último login</b> = fecha real del login. <b>Última señal</b> = heartbeat del navegador mientras la app está abierta. El cron de inactividad usa la fecha más reciente entre ambas.
      </div>
    </div>
  );
};

export default ActividadUsuarios;
